/**
 * mobile-menu-drawer.js
 * SPEC-05 — Menu Mobile Tiroir latéral
 * Web Component <mobile-menu-drawer>
 * Date: 2026-03-16
 *
 * Comportement :
 *  - Écoute l'event custom 'mobile-drawer:open' dispatché par le hamburger
 *  - Ouvre un <aside> en transform: translateX(0) (slide depuis gauche)
 *  - Ferme sur : bouton X, click overlay, Escape, resize >= 750px, click lien
 *  - Gère le trapFocus/removeTrapFocus depuis global.js
 */

class MobileMenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.triggerElement = null;
    // Bindings
    this._onDocumentOpen = this._onDocumentOpen.bind(this);
    this._onEscapeKey = this._onEscapeKey.bind(this);
    this._onResize = this._onResize.bind(this);
  }

  connectedCallback() {
    this.sectionId = this.dataset.sectionId || '';

    // Move to body to escape any position:sticky/transform containing block
    // that would break position:fixed on overlay/panel
    if (this.parentElement && this.parentElement !== document.body) {
      document.body.appendChild(this);
      return; // connectedCallback will fire again once appended to body
    }

    // Éléments internes
    this.panel = this.querySelector('.drawer-panel');
    this.overlay = this.querySelector('.drawer-overlay');
    this.closeBtn = this.querySelector('.drawer-close');

    if (!this.panel || !this.overlay) return;

    // Force high z-index to escape header stacking context
    this.overlay.style.zIndex = '999998';
    this.panel.style.zIndex = '999999';

    // T007 — bouton close
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.closeDrawer());
    }

    // T008 — overlay click
    this.overlay.addEventListener('click', () => this.closeDrawer());

    // T006 — écoute event global d'ouverture
    document.addEventListener('mobile-drawer:open', this._onDocumentOpen);

    // T017 — Escape key
    document.addEventListener('keyup', this._onEscapeKey);

    // T018 — resize ferme le tiroir sur desktop
    window.addEventListener('resize', this._onResize);

    // T021 — liens feuilles : fermer le tiroir avant navigation
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a.drawer-link:not(.drawer-link--summary)');
      if (link && this.isOpen) {
        this.closeDrawer();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('mobile-drawer:open', this._onDocumentOpen);
    document.removeEventListener('keyup', this._onEscapeKey);
    window.removeEventListener('resize', this._onResize);
  }

  /**
   * T006 — Handler event 'mobile-drawer:open'
   * Ouvre seulement si sectionId correspond (évite conflits multi-headers)
   */
  _onDocumentOpen(event) {
    const { sectionId, trigger } = event.detail || {};
    if (sectionId && sectionId !== this.sectionId) return;
    this.openDrawer(trigger || null);
  }

  /**
   * T017 — Ferme sur Escape
   */
  _onEscapeKey(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.closeDrawer();
    }
  }

  /**
   * T018 — Ferme si viewport devient >= 750px (desktop)
   */
  _onResize() {
    if (window.innerWidth >= 750 && this.isOpen) {
      this.closeDrawer();
    }
  }

  /**
   * openDrawer(triggerEl)
   * Ouvre le tiroir latéral, piège le focus
   * @param {HTMLElement|null} triggerEl - Bouton hamburger déclencheur
   */
  openDrawer(triggerEl) {
    if (this.isOpen) return;
    this.isOpen = true;
    this.triggerElement = triggerEl || null;

    // Révéler le panneau et l'overlay
    this.panel.setAttribute('aria-hidden', 'false');
    this.overlay.removeAttribute('aria-hidden');

    // Ajouter les classes d'état
    this.classList.add('drawer--open');
    document.body.classList.add('drawer-open');

    // Animation GPU-accélérée via CSS (transform: translateX(0))
    this.panel.style.transform = 'translateX(0)';
    this.overlay.style.opacity = '1';
    this.overlay.style.pointerEvents = 'auto';

    // T009 — trapFocus depuis global.js
    // On focus le panel (tabindex=-1) pour piéger le focus sans outline visible sur les liens
    if (typeof trapFocus === 'function') {
      trapFocus(this.panel, this.panel);
    } else {
      // Fallback : focus panel silencieux (pas de :focus-visible sur les liens)
      setTimeout(() => {
        this.panel.focus({ preventScroll: true });
      }, 50);
    }
  }

  /**
   * closeDrawer()
   * Ferme le tiroir, retourne le focus au déclencheur
   */
  closeDrawer() {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Masquer le panneau
    this.panel.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('aria-hidden', 'true');

    // Retirer les classes d'état
    this.classList.remove('drawer--open');
    document.body.classList.remove('drawer-open');

    // Retour à la position initiale (supports left or right side via data-side)
    const isRight = this.dataset.side === 'right';
    this.panel.style.transform = isRight ? 'translateX(100%)' : 'translateX(-100%)';
    this.overlay.style.opacity = '0';
    this.overlay.style.pointerEvents = 'none';

    // T009 — removeTrapFocus depuis global.js
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.triggerElement);
    } else if (this.triggerElement) {
      // Fallback
      this.triggerElement.focus();
    }

    // Dispatcher l'event de fermeture
    document.dispatchEvent(new CustomEvent('mobile-drawer:closed', {
      bubbles: false,
      detail: { sectionId: this.sectionId }
    }));

    this.triggerElement = null;
  }
}

// T010 — Enregistrement du Web Component
if (!customElements.get('mobile-menu-drawer')) {
  customElements.define('mobile-menu-drawer', MobileMenuDrawer);
}
